(() => {
	const mobileNavMedia = window.matchMedia("(max-width: 1399px)");

	function setMenuOpenState(isOpen) {
		if (isOpen) {
			const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
			document.body.style.setProperty("--site-scrollbar-compensation", `${scrollbarWidth}px`);
			document.body.setAttribute("data-menu-open", "");
			return;
		}

		document.body.removeAttribute("data-menu-open");
		document.body.style.removeProperty("--site-scrollbar-compensation");
	}

	function syncPrimaryMenu(toggle, menu) {
		if (!toggle || !menu) {
			return;
		}

		if (mobileNavMedia.matches) {
			if (toggle.getAttribute("aria-expanded") !== "true") {
				menu.setAttribute("hidden", "");
				setMenuOpenState(false);
			}
			return;
		}

		toggle.setAttribute("aria-expanded", "false");
		menu.removeAttribute("hidden");
		setMenuOpenState(false);
	}

	function initSiteHeader() {
		const header = document.querySelector("[data-site-header]");
		const toggle = document.querySelector(".menu-toggle");

		if (!header || !toggle) {
			return;
		}

		const controlledId = toggle.getAttribute("aria-controls");
		const menu = controlledId ? document.getElementById(controlledId) : null;

		if (!menu) {
			return;
		}

		const setMobileSubmenusExpanded = (isExpanded) => {
			menu.querySelectorAll(".site-nav__toggle").forEach((submenuToggle) => {
				const submenuId = submenuToggle.getAttribute("aria-controls");
				const submenu = submenuId ? document.getElementById(submenuId) : null;

				submenuToggle.setAttribute("aria-expanded", isExpanded ? "true" : "false");

				if (!submenu) {
					return;
				}

				if (isExpanded) {
					submenu.removeAttribute("hidden");
				} else {
					submenu.setAttribute("hidden", "");
				}
			});
		};

		const expandCurrentMobileSubmenus = () => {
			menu.querySelectorAll(".site-nav__item.current-menu-ancestor, .site-nav__item.current-menu-parent").forEach((item) => {
				const submenuToggle = item.querySelector(":scope > .site-nav__toggle");
				const submenuId = submenuToggle ? submenuToggle.getAttribute("aria-controls") : null;
				const submenu = submenuId ? document.getElementById(submenuId) : null;

				if (!submenuToggle || !submenu) {
					return;
				}

				submenuToggle.setAttribute("aria-expanded", "true");
				submenu.removeAttribute("hidden");
			});
		};

		const closeMenu = () => {
			toggle.setAttribute("aria-expanded", "false");
			if (mobileNavMedia.matches) {
				menu.setAttribute("hidden", "");
				setMobileSubmenusExpanded(false);
			}
			setMenuOpenState(false);
		};

		const openMenu = () => {
			toggle.setAttribute("aria-expanded", "true");
			menu.removeAttribute("hidden");
			if (mobileNavMedia.matches) {
				setMobileSubmenusExpanded(false);
				expandCurrentMobileSubmenus();
			}
			setMenuOpenState(true);
		};

		const syncStickyState = () => {
			header.classList.toggle("site-header--sticky", window.scrollY > 12);
		};

		if (!mobileNavMedia.matches) {
			setMobileSubmenusExpanded(false);
		}

		syncPrimaryMenu(toggle, menu);
		syncStickyState();

		toggle.addEventListener("click", () => {
			const isExpanded = toggle.getAttribute("aria-expanded") === "true";
			if (isExpanded) {
				closeMenu();
			} else {
				openMenu();
			}
		});

		mobileNavMedia.addEventListener("change", () => {
			if (!mobileNavMedia.matches) {
				setMobileSubmenusExpanded(false);
			}

			syncPrimaryMenu(toggle, menu);
		});
		window.addEventListener("scroll", syncStickyState, { passive: true });

		menu.querySelectorAll("a").forEach((link) => {
			link.addEventListener("click", () => {
				if (mobileNavMedia.matches) {
					closeMenu();
				}
			});
		});

		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape" && toggle.getAttribute("aria-expanded") === "true") {
				closeMenu();
				toggle.focus();
			}
		});
	}

	function initThemeToggle() {
		const toggle = document.querySelector("[data-theme-toggle]");
		const choices = [...document.querySelectorAll("[data-theme-choice]")];
		const root = document.documentElement;
		const storageKey = "noxrt-theme";

		if (!toggle && choices.length === 0) {
			return;
		}

		const getPreferredTheme = () => {
			const storedTheme = window.localStorage.getItem(storageKey);
			if (storedTheme === "light" || storedTheme === "dark") {
				return storedTheme;
			}

			return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
		};

		const applyTheme = (theme) => {
			root.dataset.siteTheme = theme;

			if (toggle) {
				toggle.setAttribute("aria-pressed", theme === "dark" ? "true" : "false");
				const nextLabel =
					theme === "dark"
						? toggle.dataset.labelLight || "Switch to light theme"
						: toggle.dataset.labelDark || "Switch to dark theme";
				toggle.setAttribute("aria-label", nextLabel);
				toggle.setAttribute("title", nextLabel);
			}

			choices.forEach((choice) => {
				choice.setAttribute("aria-pressed", choice.dataset.themeChoice === theme ? "true" : "false");
			});
		};

		applyTheme(getPreferredTheme());

		if (toggle) {
			toggle.addEventListener("click", () => {
				const nextTheme = root.dataset.siteTheme === "dark" ? "light" : "dark";
				window.localStorage.setItem(storageKey, nextTheme);
				applyTheme(nextTheme);
			});
		}

		choices.forEach((choice) => {
			choice.addEventListener("click", () => {
				const selectedTheme = choice.dataset.themeChoice;
				if (selectedTheme !== "light" && selectedTheme !== "dark") {
					return;
				}
				window.localStorage.setItem(storageKey, selectedTheme);
				applyTheme(selectedTheme);
			});
		});
	}

	function initAccountDrawer() {
		const toggle = document.querySelector("[data-account-drawer-toggle]");
		const drawer = document.querySelector("[data-account-drawer]");
		const closeButton = document.querySelector("[data-account-drawer-close]");
		const backdrop = document.querySelector("[data-account-drawer-backdrop]");

		if (!toggle || !drawer || !closeButton || !backdrop) {
			return;
		}

		let closeTimer;

		const closeDrawer = () => {
			window.clearTimeout(closeTimer);
			toggle.setAttribute("aria-expanded", "false");
			drawer.classList.remove("is-open");
			backdrop.classList.remove("is-open");
			document.body.removeAttribute("data-account-drawer-open");
			closeTimer = window.setTimeout(() => {
				drawer.hidden = true;
				backdrop.hidden = true;
			}, 350);
			toggle.focus();
		};

		const openDrawer = () => {
			window.clearTimeout(closeTimer);
			drawer.hidden = false;
			backdrop.hidden = false;
			document.body.setAttribute("data-account-drawer-open", "");
			toggle.setAttribute("aria-expanded", "true");
			window.requestAnimationFrame(() => {
				drawer.classList.add("is-open");
				backdrop.classList.add("is-open");
				closeButton.focus();
			});
		};

		toggle.addEventListener("click", () => {
			if (toggle.getAttribute("aria-expanded") === "true") {
				closeDrawer();
			} else {
				openDrawer();
			}
		});
		closeButton.addEventListener("click", closeDrawer);
		backdrop.addEventListener("click", closeDrawer);
		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape" && toggle.getAttribute("aria-expanded") === "true") {
				closeDrawer();
			}
		});
	}

	function initSubmenuToggles() {
		document.querySelectorAll(".site-nav__toggle").forEach((button) => {
			const controlledId = button.getAttribute("aria-controls");
			const submenu = controlledId ? document.getElementById(controlledId) : null;

			if (!submenu) {
				return;
			}

			button.addEventListener("click", () => {
				const isExpanded = button.getAttribute("aria-expanded") === "true";

				if (!isExpanded && mobileNavMedia.matches) {
					document.querySelectorAll(".site-nav__toggle[aria-expanded='true']").forEach((openButton) => {
						if (openButton === button) {
							return;
						}

						const openControlledId = openButton.getAttribute("aria-controls");
						const openSubmenu = openControlledId ? document.getElementById(openControlledId) : null;

						openButton.setAttribute("aria-expanded", "false");

						if (openSubmenu) {
							openSubmenu.setAttribute("hidden", "");
						}
					});
				}

				button.setAttribute("aria-expanded", isExpanded ? "false" : "true");
				if (isExpanded) {
					submenu.setAttribute("hidden", "");
				} else {
					submenu.removeAttribute("hidden");
				}
			});
		});
	}

	function initDiscordStats() {
		const stats = document.querySelector("[data-discord-stats]");

		if (!stats || !stats.dataset.discordStatsEndpoint) {
			return;
		}

		const online = stats.querySelector("[data-discord-online]");
		const members = stats.querySelector("[data-discord-members]");
		const numberFormatter = new Intl.NumberFormat(document.documentElement.lang || undefined);

		fetch(stats.dataset.discordStatsEndpoint, {
			headers: {
				Accept: "application/json",
			},
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error("Discord stats request failed");
				}

				return response.json();
			})
			.then((data) => {
				if (online && Number.isFinite(data.online)) {
					online.textContent = numberFormatter.format(data.online);
				}

				if (members && Number.isFinite(data.members)) {
					members.textContent = numberFormatter.format(data.members);
				}
			})
			.catch(() => {
				stats.setAttribute("data-discord-stats-error", "");
			});
	}

	function initAutoTabbedContent() {
		if (!window.customElements || !window.customElements.whenDefined) {
			return;
		}

		window.customElements.whenDefined("ob-tabbed-content").then(() => {
			document.querySelectorAll("ob-tabbed-content[data-auto-tabs]").forEach((tabbedContent) => {
				const tabList = tabbedContent.querySelector("[data-tabbed-content-tab-list]");
				const panes = Array.from(tabbedContent.querySelectorAll("[data-tabbed-content-pane]"));

				if (!tabList || !panes.length || tabList.children.length) {
					return;
				}

				const activePaneIndex = Math.max(
					0,
					panes.findIndex((pane) => pane.hasAttribute("data-tabbed-content-active")),
				);

				panes.forEach((pane, index) => {
					if (!pane.id) {
						pane.id = `tabbed-content-pane-${index + 1}`;
					}

					const label = pane.getAttribute("data-label") || `Tab ${index + 1}`;
					const tab = document.createElement("li");
					const link = document.createElement("a");
					const isActive = index === activePaneIndex;

					tab.setAttribute("data-tabbed-content-tab", "");
					link.href = `#${pane.id}`;
					link.className = "ob-tab";
					link.setAttribute("role", "tab");
					link.setAttribute("aria-controls", pane.id);
					link.textContent = label;

					if (isActive) {
						tab.setAttribute("data-tabbed-content-active", "");
						pane.setAttribute("data-tabbed-content-active", "");
						pane.removeAttribute("hidden");
						link.setAttribute("aria-selected", "true");
					} else {
						pane.setAttribute("hidden", "");
						link.setAttribute("aria-selected", "false");
						link.setAttribute("tabindex", "-1");
					}

					tab.appendChild(link);
					tabList.appendChild(tab);
				});

				tabList.classList.add("ob-tab-bar");
				tabbedContent.connectedCallback();
				const syncTabs = () => {
					tabbedContent.querySelectorAll("[data-tabbed-content-tab]").forEach((tab) => {
						const link = tab.querySelector(".ob-tab");
						const isActive = tab.hasAttribute("data-tabbed-content-active");

						if (!link) {
							return;
						}

						link.classList.toggle("ob-tab--active", isActive);
						link.setAttribute("aria-selected", isActive ? "true" : "false");
						link.setAttribute("tabindex", isActive ? "0" : "-1");
					});
				};

				syncTabs();
				tabbedContent.addEventListener("toggle", syncTabs);
			});
		});
	}

	function initScrollRail() {
		const rail = document.querySelector("[data-scroll-rail]");
		const track = rail ? rail.querySelector("[data-scroll-rail-track]") : null;
		const car = rail ? rail.querySelector("[data-scroll-rail-car]") : null;
		const tireTracks = rail ? rail.querySelectorAll("[data-scroll-rail-tire]") : [];

		if (!rail || !track || !car || !tireTracks.length) {
			return;
		}

		let frameRequested = false;

		const update = () => {
			frameRequested = false;

			if (window.getComputedStyle(rail).display === "none") {
				return;
			}

			const root = document.documentElement;
			const scrollableHeight = Math.max(root.scrollHeight - window.innerHeight, 0);
			const progress = scrollableHeight > 0 ? Math.min(Math.max(window.scrollY / scrollableHeight, 0), 1) : 0;
			const trackStyle = window.getComputedStyle(rail);
			const rootFontSize = Number.parseFloat(window.getComputedStyle(root).fontSize) || 16;
			const toPixels = (value) => {
				const numericValue = Number.parseFloat(value) || 0;
				return value.trim().endsWith("rem") ? numericValue * rootFontSize : numericValue;
			};
			const start = toPixels(trackStyle.getPropertyValue("--scroll-rail-start"));
			const end = toPixels(trackStyle.getPropertyValue("--scroll-rail-end"));
			const carHeight = car.getBoundingClientRect().height;
			const travel = Math.max(track.clientHeight - start - end - carHeight, 0);
			const carTop = start + progress * travel;
			const tireLength = Math.max(carTop - start + carHeight / 2, 0);

			car.style.top = `${carTop}px`;
			tireTracks.forEach((tireTrack) => {
				tireTrack.style.height = `${tireLength}px`;
			});
		};

		const requestUpdate = () => {
			if (frameRequested) {
				return;
			}

			frameRequested = true;
			window.requestAnimationFrame(update);
		};

		window.addEventListener("scroll", requestUpdate, { passive: true });
		window.addEventListener("resize", requestUpdate);
		window.addEventListener("load", requestUpdate);

		if (window.ResizeObserver) {
			const resizeObserver = new ResizeObserver(requestUpdate);
			resizeObserver.observe(document.documentElement);
		}

		requestUpdate();
	}

	document.addEventListener("DOMContentLoaded", () => {
		initThemeToggle();
		initAccountDrawer();
		initSiteHeader();
		initSubmenuToggles();
		initDiscordStats();
		initAutoTabbedContent();
		initScrollRail();
	});
})();
